import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { IS_DEV } from '../../config/env';
import { getArtifactRoute, isAttachmentLike } from '../../utils/artifactCapabilities';
import { FROM_WORKSPACE_STATE, type OpenedFrom } from '../../utils/workspaceNavState';
import { openLinkArtifact } from './openLinkArtifact';
import { useOpenAttachment } from './useOpenAttachment';

interface UseEntityNavigationOptions {
  /**
   * The caller is the workspace itself (the canvas views and the sidebar):
   * the history entry is marked
   * {@link FROM_WORKSPACE_STATE}, which is what keeps the workspace sidebar
   * on the artifact page. Every other surface leaves it off, so an artifact
   * reached from search, chat or a link shows no sidebar.
   */
  readonly fromWorkspace?: boolean;
  /**
   * The caller is a section list outside the workspace (the Protocols page):
   * the entry carries {@link OpenedFrom} as router state, so the item's trail
   * starts at that list. Ignored when `fromWorkspace` is set.
   */
  readonly from?: OpenedFrom;
}

/** Per-call extras for {@link useEntityNavigation}'s `openEntity`. */
export interface OpenEntityOptions {
  /**
   * A `link`'s stored URL, when the caller already holds it (workspace rows
   * carry `FileItem.link`). Without it the link opens `/links/:id`, which
   * loads the URL and replaces itself with it.
   */
  readonly href?: string;
  /** Where the open came from, for the Link Opened event. */
  readonly surface?: string;
}

/**
 * The single way to open an **artifact** from a list / table / card / chip.
 *
 * Every artifact type navigates to its detail route, attachments included: the
 * split view is reserved for a note / protocol opening its OWN attachment (see
 * `useOpenAttachment`), and nothing reaching this hook is in that position — a
 * table row, tree node or chip has no relationship to the page behind it. This
 * composition used to be copy-pasted — once in a row-click handler and again in
 * a name-cell render — and PR #210 fixed a bug caused by two of those copies
 * drifting. Route every imperative artifact open through this hook so there is
 * only one copy to keep correct.
 *
 * **Scope — artifacts only.** Collections (`project`) navigate to
 * `/collections/:id`: entity tables render them as declarative `<RouterLink>`s
 * (correct for an anchor — middle-click / open-in-new-tab work), and the few
 * imperative callers (ContextChip, useLinkedEntityOpener) keep a one-line
 * `project` branch before delegating here. Adding a collection branch to this hook is a
 * deliberate non-goal; an unknown type warns in dev (see below).
 *
 * `openEntity` is stable across renders **as long as the side-panel context and
 * the desktop/mobile breakpoint don't change** (it composes `useOpenAttachment`,
 * which depends on both). That's the correct behaviour — listing it in a
 * `columns` `useMemo`'s deps needs no `eslint-disable`, and the memo recomputes
 * only when the underlying navigation target genuinely changes. `from` depends
 * on its own primitives (`key`, `to`), not the object identity, so a caller
 * that rebuilds it fresh each render doesn't break that stability either.
 */
export function useEntityNavigation({
  fromWorkspace = false,
  from,
}: UseEntityNavigationOptions = {}) {
  const navigate = useNavigate();
  const openAttachment = useOpenAttachment();
  // Depending on the primitives, not `from` itself, keeps `openEntity` stable
  // when the caller rebuilds the object each render.
  const key = from?.key;
  const to = from?.to;

  const openEntity = useCallback(
    (entityType: string, id: string, options?: OpenEntityOptions) => {
      // A link leaves Bower in a new tab (docs/planning/link-artifacts.md, D6).
      if (entityType === 'link') {
        openLinkArtifact(id, options);
        return;
      }
      let state: typeof FROM_WORKSPACE_STATE | { openedFrom: OpenedFrom } | undefined;
      if (fromWorkspace) {
        state = FROM_WORKSPACE_STATE;
      } else if (key !== undefined && to !== undefined) {
        state = { openedFrom: { key, to } };
      }
      const navOptions = state ? { state } : undefined;
      if (isAttachmentLike(entityType)) {
        if (navOptions) openAttachment(id, navOptions);
        else openAttachment(id);
        return;
      }
      const route = getArtifactRoute(entityType, id);
      if (route) {
        if (navOptions) navigate(route, navOptions);
        else navigate(route);
        return;
      }
      // Not attachment-like and no detail route: a type we don't know how to
      // open. Previously this silently no-oped (the failure mode behind #210).
      // Surface it loudly in dev; stay a no-op in prod (nav must never throw).
      if (IS_DEV) {
        console.warn(`useEntityNavigation: no route for entity type "${entityType}"`);
      }
    },
    [fromWorkspace, key, to, navigate, openAttachment],
  );

  return { openEntity };
}
